import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Printer, Trash2, Pencil } from "lucide-react";

const TYPES = ["browser", "network", "usb", "bluetooth", "agent"];
const STATIONS = ["main", "grill", "tandoor", "bar", "packing"];
const emptyForm = () => ({
  name: "",
  type: "browser",
  connection: "",
  station: "main",
  roles: ["kot", "invoice"],
  categories: "",
  paper_width: 80,
  copies: 1,
  active: true,
});

export default function Printers() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const { data: printers = [], isLoading } = useQuery({
    queryKey: ["printers"],
    queryFn: async () => (await api.get("/printers")).data,
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      type: p.type || "browser",
      connection: p.connection || "",
      station: p.station || "main",
      roles: Array.isArray(p.roles) ? [...p.roles] : ["kot", "invoice"],
      categories: Array.isArray(p.categories) ? p.categories.join(", ") : "",
      paper_width: p.paper_width || 80,
      copies: p.copies || 1,
      active: p.active !== false,
    });
    setOpen(true);
  };

  const toggleRole = (role) => {
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(role) ? f.roles.filter((r) => r !== role) : [...f.roles, role],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = {
      name: form.name.trim(),
      type: form.type,
      connection: form.connection.trim(),
      station: form.station,
      roles: form.roles,
      categories: form.categories.split(",").map((s) => s.trim()).filter(Boolean),
      paper_width: Number(form.paper_width) || 80,
      copies: Number(form.copies) || 1,
      active: form.active,
    };
    try {
      if (editing) {
        await api.put(`/printers/${editing.id}`, payload);
        toast.success("Printer updated");
      } else {
        await api.post("/printers", payload);
        toast.success("Printer added");
      }
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["printers"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete printer “${p.name}”?`)) return;
    try {
      await api.delete(`/printers/${p.id}`);
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["printers"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="printers-page">
      <PageHeader title="Printers & KOT Stations" subtitle="Route kitchen tickets and invoices by station. For thermal printers run print-agent locally.">

        <Button onClick={openCreate} className="bg-[var(--kul-primary)] text-white">
          <Plus className="w-4 h-4 mr-1" /> Add printer
        </Button>
      </PageHeader>

      
      <div className="kul-card p-4 mb-5 max-w-5xl text-sm text-neutral-600" data-testid="print-agent-help">
        <div className="font-semibold text-neutral-800 mb-1">Local print agent</div>
        <p>On the kitchen PC: <code className="text-xs bg-neutral-100 px-1 rounded">cd print-agent && PRINTER_HOST=192.168.1.50 npm start</code></p>
        <p className="mt-1">Add a printer with type <b>agent</b> and connection <code className="text-xs bg-neutral-100 px-1 rounded">http://127.0.0.1:9100/print</code>.</p>
      </div>

      {isLoading ? (
        <p className="text-neutral-400">Loading…</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
          {printers.map((p) => (
            <div key={p.id} className="kul-card p-4 space-y-2" data-testid={`printer-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-[var(--kul-primary)]/10 text-[var(--kul-primary)] flex items-center justify-center shrink-0">
                    <Printer className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{p.name}</div>
                    <div className="text-xs text-neutral-400 capitalize">{p.type} · {p.station}</div>
                  </div>
                </div>
                <Badge className={p.active !== false ? "bg-green-100 text-green-700" : "bg-neutral-100 text-neutral-500"}>
                  {p.active !== false ? "Active" : "Off"}
                </Badge>
              </div>
              {p.connection && <div className="text-xs text-neutral-500 font-mono truncate">{p.connection}</div>}
              <div className="flex flex-wrap gap-1">
                {(p.roles || []).map((r) => (
                  <Badge key={r} className="bg-neutral-100 text-neutral-600 capitalize text-[10px]">{r}</Badge>
                ))}
                {(p.categories || []).map((c) => (
                  <Badge key={c} className="bg-orange-50 text-[var(--kul-primary)] text-[10px]">{c}</Badge>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                <Button size="sm" variant="outline" onClick={() => remove(p)} className="text-[var(--kul-danger)]">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
          {!printers.length && (
            <p className="text-neutral-400 col-span-full">No printers yet. Browser print is used as fallback.</p>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit printer" : "Add printer"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Kitchen main" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Type</Label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm">
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Station</Label>
                <select value={form.station} onChange={(e) => setForm({ ...form, station: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm">
                  {STATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Connection (IP:port or agent URL)</Label>
              <Input value={form.connection} onChange={(e) => setForm({ ...form, connection: e.target.value })} placeholder="http://127.0.0.1:9100/print" />
            </div>
            <div>
              <Label>Roles</Label>
              <div className="flex gap-3 mt-1">
                {["kot", "invoice"].map((r) => (
                  <label key={r} className="flex items-center gap-1.5 text-sm capitalize">
                    <input type="checkbox" checked={form.roles.includes(r)} onChange={() => toggleRole(r)} />
                    {r}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Categories (comma-separated)</Label>
              <Input value={form.categories} onChange={(e) => setForm({ ...form, categories: e.target.value })} placeholder="grill, tandoor" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Paper width</Label><Input type="number" value={form.paper_width} onChange={(e) => setForm({ ...form, paper_width: e.target.value })} /></div>
              <div><Label>Copies</Label><Input type="number" value={form.copies} onChange={(e) => setForm({ ...form, copies: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Active
            </label>
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-[var(--kul-primary)] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
